import {
  amberTokens,
  blueTokens,
  cyanTokens,
  defaultTokens,
  fuchsiaTokens,
  greenTokens,
  limeTokens,
  pinkTokens,
  violetTokens,
  type AppTheme,
  type ChartConfig
} from '@knack/asterisk-react';
import { type ColorDeclarationObject } from '@knack/asterisk-react/dist/src/themes/types';
import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';

import { type ChartLayout } from '@/types/schema/views/ReportView';

export function getChartColors(
  theme: AppTheme,
  isDarkMode: boolean,
  colors: ChartLayout['colors'] = 'monochromatic'
) {
  const themeMode = isDarkMode ? 'dark' : 'light';

  switch (theme.colors.preset) {
    case 'default':
    case 'custom':
      return defaultTokens.charts[themeMode][colors];
    case 'blue':
      return blueTokens.charts[themeMode][colors];
    case 'green':
      return greenTokens.charts[themeMode][colors];
    case 'amber':
      return amberTokens.charts[themeMode][colors];
    case 'lime':
      return limeTokens.charts[themeMode][colors];
    case 'pink':
      return pinkTokens.charts[themeMode][colors];
    case 'cyan':
      return cyanTokens.charts[themeMode][colors];
    case 'fuchsia':
      return fuchsiaTokens.charts[themeMode][colors];
    case 'violet':
      return violetTokens.charts[themeMode][colors];

    default:
      throw new Error(`Unknown preset: ${theme.colors.preset}`);
  }
}

/**
 * Get the background card color for a specific theme and mode.
 *
 * This function retrieves the card background color from the theme tokens
 * based on the provided theme preset and dark/light mode preference.
 * Each theme preset has its own set of color tokens for both light and dark modes.
 *
 * @param theme - The application theme object containing the color preset
 * @param isDarkMode - Whether to use dark mode (true) or light mode (false) colors
 * @returns The card background color as a string (e.g., "#FFFFFF", "rgb(255,255,255)")
 * @throws Error if the theme preset is unknown/unsupported
 *
 * @example
 * ```typescript
 * const theme = { colors: { preset: 'blue', customTokens: null } };
 * const lightBgColor = getBgCardColor(theme, false); // Returns light mode card color
 * const darkBgColor = getBgCardColor(theme, true);   // Returns dark mode card color
 * ```
 */
export function getBgCardColor(theme: AppTheme, isDarkMode: boolean): string {
  const themeMode = isDarkMode ? 'dark' : 'light';

  switch (theme.colors.preset) {
    case 'default':
      return (defaultTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'custom':
      return (theme.colors.customTokens?.[themeMode].bg as ColorDeclarationObject).card as string;
    case 'blue':
      return (blueTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'green':
      return (greenTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'amber':
      return (amberTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'lime':
      return (limeTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'pink':
      return (pinkTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'cyan':
      return (cyanTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'fuchsia':
      return (fuchsiaTokens[themeMode].bg as ColorDeclarationObject).card as string;
    case 'violet':
      return (violetTokens[themeMode].bg as ColorDeclarationObject).card as string;

    default:
      throw new Error(`Unknown preset: ${theme.colors.preset}`);
  }
}

// Extend colord with accessibility plugin at module level
extend([a11yPlugin]);

/*
 * Generates a contrasting color based on the luminance of the provided hex color.
 * If the luminance is above a certain threshold, it returns black; otherwise, it returns white.
 */
export function generateContrastColor(normalizedColor?: string): 'black' | 'white' {
  if (!normalizedColor) {
    return 'black'; // Default fallback
  }

  const color = colord(normalizedColor);
  const luminance = color.luminance();

  // Generate contrasting color based on luminance
  // Based on WCAG 2.1 relative luminance threshold for contrast ratio calculations
  // This value (0.179) represents the luminance threshold where contrast ratio = 4.5:1
  // Calculation: (4.5 - 1)² / (4.5 + 1)² = 12.25 / 30.25 ≈ 0.179
  // See: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
  const WCAG_LUMINANCE_THRESHOLD = 0.179;
  if (luminance > WCAG_LUMINANCE_THRESHOLD) {
    return 'black'; // Darken
  }
  return 'white'; // Lighten
}

/**
 * Get chart color for a specific key from chart config
 */
export function getChartColorByKey(chartConfig: ChartConfig, key: string): string | undefined {
  return chartConfig[key]?.color;
}

/**
 * Get all chart colors from chart config
 */
export function getAllChartColors(chartConfig: ChartConfig): Record<string, string> {
  return Object.entries(chartConfig).reduce<Record<string, string>>((acc, [key, config]) => {
    if (config.color) {
      acc[key] = config.color;
    }
    return acc;
  }, {});
}
